import { useRef, useMemo } from 'react';
import { useInView } from 'framer-motion';

const variants = {
    fadeUp: {
        hidden: { opacity: 0, transform: 'translateY(30px)', filter: 'blur(3px)' },
        visible: { opacity: 1, transform: 'translateY(0px)', filter: 'blur(0px)' },
    },
    scaleUp: {
        hidden: { opacity: 0, transform: 'scale(0.94)', filter: 'blur(3px)' },
        visible: { opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' },
    },
    slideLeft: {
        hidden: { opacity: 0, transform: 'translateX(40px)', filter: 'blur(3px)' },
        visible: { opacity: 1, transform: 'translateX(0px)', filter: 'blur(0px)' },
    },
    slideRight: {
        hidden: { opacity: 0, transform: 'translateX(-40px)', filter: 'blur(3px)' },
        visible: { opacity: 1, transform: 'translateX(0px)', filter: 'blur(0px)' },
    },
    fadeIn: {
        hidden: { opacity: 0, filter: 'blur(3px)' },
        visible: { opacity: 1, filter: 'blur(0px)' },
    },
    clipReveal: {
        hidden: { opacity: 0, clipPath: 'inset(0 100% 0 0)' },
        visible: { opacity: 1, clipPath: 'inset(0 0% 0 0)' },
    },
    clipRevealDown: {
        hidden: { opacity: 0, clipPath: 'inset(0 0 100% 0)' },
        visible: { opacity: 1, clipPath: 'inset(0 0 0% 0)' },
    },
};

const BASE_TRANSITION = 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1), filter 0.7s cubic-bezier(0.16, 1, 0.3, 1), clip-path 0.8s cubic-bezier(0.16, 1, 0.3, 1)';

export function useScrollReveal(variant = 'fadeUp', margin = '-80px') {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin });

    const v = variants[variant] || variants.fadeUp;
    const style = {
        ...(isInView ? v.visible : v.hidden),
        transition: BASE_TRANSITION,
    };

    return { ref, style, isInView };
}

export function useStaggeredReveal(variant = 'fadeUp', staggerDelay = 0.12, margin = '-80px') {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin });
    const v = variants[variant] || variants.fadeUp;

    const getChildStyle = useMemo(() => {
        return (index) => ({
            ...(isInView ? v.visible : v.hidden),
            transition: `opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${index * staggerDelay}s, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${index * staggerDelay}s, filter 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${index * staggerDelay}s, clip-path 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${index * staggerDelay}s`,
        });
    }, [isInView, v, staggerDelay]);

    return { ref, isInView, getChildStyle };
}
